import { Box, CircularProgress, useTheme } from '@mui/material';
import { FolderLock, HardDrives, Stack } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDarkMode } from '../../../../hooks/useDarkMode';
import { useSidebarData } from '../../../../hooks/useSidebarData';
import { vectorQueries } from '../../../../queries/vector';
import { useAuthStore } from '../../../../store/storeAuth';
import { useObjectStore } from '../../../../store/storeObjects';
import { Namespace } from '../../../../types/namespace';
import { Flows, Folder } from '../../../../util/Types/Folder';
import { Space } from '../../../../util/Types/Sidebar';
import { useExtendedSidebarContext } from '../ExtendedSidebar';
import { useFlowModal } from '../FlowModalContext';
import { AnimatedListItem } from '../dnd/AnimatedListItem';
import { DragDropProvider } from '../dnd/DragDropProvider';
import {
  getReservedFolderType,
  isFluxbotInVirtualHome,
} from '../dnd/dropRules';
import { useSidebarAutoScroll } from '../dnd/useSidebarAutoScroll';
import { CollapsibleGroup } from '../shared/CollapsibleGroup';
import { useSidebarFolderActions } from '../shared/useSidebarFolderActions';
import { useSidebarRefresh } from '../shared/useSidebarRefresh';
import { useSortedSpaces } from '../shared/useSortedSpaces';
import type { CreateItemType } from '../types';
import { enrichFoldersWithItems } from '../util/enrichFoldersWithItems';
import {
  getFluxbotVirtualFolderId,
  isFormFlow,
  parseSpaceIdFromFolderId,
} from '../utils';
import { ActionMenu } from './ActionMenu';
import { AgentItem } from './AgentItem';
import { AgentsSectionBody } from './AgentsSectionBody';
import { AgentsSidebarModals } from './AgentsSidebarModals';
import { FluxBotItem } from './FluxBotItem';
import { FolderItem } from './FolderItem';
import { Item } from './Item';
import { Section } from './Section';
import { SidebarAddButton } from './SidebarAddButton';
import {
  AgentsSidebarContext,
  AgentsSidebarContextValue,
  FluxObject,
  useAgentsSidebarContext,
} from './context';
import { useAgentsSidebarData, useAgentsSidebarHandlers } from './hooks';

const VectorMemoryItem: React.FC<{
  namespace: Namespace;
  onEdit: (ns: Namespace) => void;
  onDelete: (ns: Namespace) => void;
}> = ({ namespace, onEdit, onDelete }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <>
      <Item
        icon={<HardDrives size={16} />}
        label={namespace.name}
        isNested
        onMenuClick={anchor => {
          setAnchorEl(anchor);
        }}
      />
      <ActionMenu
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onEdit={() => {
          setAnchorEl(null);
          onEdit(namespace);
        }}
        onDelete={() => {
          setAnchorEl(null);
          onDelete(namespace);
        }}
        showEdit={true}
      />
    </>
  );
};

export const AgentsSidebarBody: React.FC = () => {
  const theme = useTheme();
  const { customColorPalette: palette } = theme;
  const isDarkMode = useDarkMode();
  const setScrollRef = useSidebarAutoScroll();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ flowId?: string }>();

  const setFolders = useAuthStore(s => s.setFolders);
  const storeFolders = useAuthStore(s => s.folders);
  const authUser = useAuthStore(s => s.user);
  const setAuthUser = useAuthStore(s => s.setAuthUser);
  const { searchTerm } = useExtendedSidebarContext();
  const { resetObjectUpdate, objects: objectStore } = useObjectStore(s => s);
  const {
    folders,
    spaces,
    user: sidebarUser,
    isLoading: sidebarLoading,
    refresh: refreshSidebar,
    refreshTree,
  } = useSidebarData();

  const { data: privateNamespaces } = useQuery(vectorQueries.allNamespaces());

  const [activeFlowId, setActiveFlowId] = useState<string | undefined>();
  const [activeObjectId, setActiveObjectId] = useState<string | undefined>();
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [addUsersSpaceId, setAddUsersSpaceId] = useState<string | null>(null);
  const [copySpaceIdModalSpace, setCopySpaceIdModalSpace] = useState<{
    name: string;
    spaceId: string;
  } | null>(null);
  const [showUsersAddedModal, setShowUsersAddedModal] = useState(false);
  const [createItemRequest, setCreateItemRequest] = useState<{
    type: CreateItemType;
    folderId?: string;
    spaceId?: string;
  } | null>(null);
  const [vectorCreateRequest, setVectorCreateRequest] = useState<{
    spaceId?: string;
  } | null>(null);
  const [fluxbotCreateRequest, setFluxbotCreateRequest] = useState<{
    spaceId?: string;
  } | null>(null);
  const [pendingVectorNamespace, setPendingVectorNamespace] =
    useState<Namespace | null>(null);

  const flowModal = useFlowModal();

  const getUserObjects = useCallback(
    (): FluxObject[] => authUser?.objects ?? objectStore['user'] ?? [],
    [objectStore, authUser?.objects],
  );
  const getSpaceObjects = useCallback(
    (spaceId: string): FluxObject[] => {
      const fromSpace = spaces.find(s => s.id === spaceId)?.objects;
      if (fromSpace?.length) return fromSpace as FluxObject[];
      return objectStore[spaceId] || [];
    },
    [objectStore, spaces],
  );
  const fluxObjects = getUserObjects();

  const {
    handlers,
    modals,
    setEditingObjectAndClose,
    setEditingVectorMemoryAndClose,
    performVectorMemoryDelete,
  } = useAgentsSidebarHandlers(activeFlowId, flowModal, {
    openDialog: ns => setPendingVectorNamespace(ns),
  });

  const sidebarData = useAgentsSidebarData(
    folders,
    spaces,
    searchTerm,
    fluxObjects,
    getSpaceObjects,
    privateNamespaces?.data ?? (authUser?.namespaces as Namespace[]) ?? [],
  );

  const { privateItemsRaw, allSpacesItemsRaw } = sidebarData;

  const privateFluxbots = useMemo(
    () =>
      privateItemsRaw.fluxbots.filter(f => isFluxbotInVirtualHome(f, folders)),
    [privateItemsRaw.fluxbots, folders],
  );

  const fluxbotsBySpaceId = useMemo(() => {
    const map: Record<string, Flows[]> = {};
    for (let i = 0; i < allSpacesItemsRaw.length; i++) {
      const spaceItems = allSpacesItemsRaw[i];
      const space = spaces[i];
      if (!space) continue;
      const spaceFolders = space.folder ?? [];
      map[space.id] = spaceItems.fluxbots.filter(f =>
        isFluxbotInVirtualHome(f, spaceFolders),
      );
    }
    return map;
  }, [allSpacesItemsRaw, spaces]);

  const lastAppliedUserRef = useRef<string>('');
  useEffect(() => {
    if (!sidebarUser) return;
    const signature = JSON.stringify(sidebarUser);
    if (signature === lastAppliedUserRef.current) return;
    lastAppliedUserRef.current = signature;
    setAuthUser(sidebarUser);
  }, [sidebarUser, setAuthUser]);

  const lastAppliedFoldersRef = useRef<string>('');
  useEffect(() => {
    const signature = folders.map(f => f.id).join('|');
    if (signature === lastAppliedFoldersRef.current) return;
    lastAppliedFoldersRef.current = signature;
    if (folders.length > 0) setFolders(folders);
    else if (folders.length === 0 && storeFolders.length > 0) setFolders([]);
  }, [folders, setFolders, storeFolders.length]);

  useSidebarRefresh(refreshTree);

  useEffect(() => {
    if (
      params.flowId &&
      (location.pathname.includes('/agent/') ||
        location.pathname.includes('/spaceAgent/') ||
        location.pathname.includes('/interface/'))
    ) {
      setActiveFlowId(params.flowId);
    } else {
      setActiveFlowId(undefined);
    }
  }, [params.flowId, location.pathname]);

  const handleFlowClick = useCallback(
    (flow: Flows, folderId?: string) => {
      setActiveFlowId(flow.id);
      setActiveObjectId(undefined);
      const isForm = isFormFlow(flow);
      let spaceId: string | undefined;
      if (folderId) {
        spaceId = parseSpaceIdFromFolderId(folderId);
        if (!spaceId && spaces.some(s => s.id === folderId)) spaceId = folderId;
        if (!spaceId) {
          const folder =
            folders.find(f => f.id === folderId) ||
            spaces
              .flatMap(s => s.folder || [])
              .find((f: Folder) => f.id === folderId);
          if (folder?.space) spaceId = folder.space;
        }
      }
      if (!spaceId)
        spaceId =
          flow.spaceId ||
          (typeof flow.space === 'string' ? flow.space : undefined);
      const isSpaceFlow = !!spaceId;
      const route = isForm
        ? isSpaceFlow
          ? `/dashboard-beta/interface/${flow.id}${
              spaceId ? `?spaceId=${spaceId}` : ''
            }`
          : `/dashboard-beta/interface/${flow.id}`
        : isSpaceFlow
          ? `/dashboard-beta/spaceAgent/${flow.id}${
              spaceId ? `?spaceId=${spaceId}` : ''
            }`
          : `/dashboard-beta/agent/${flow.id}`;
      navigate(route);
    },
    [navigate, spaces, folders],
  );

  const handleObjectClick = useCallback((obj: FluxObject) => {
    setActiveObjectId(obj.id);
    setActiveFlowId(undefined);
  }, []);

  const folderActions = useSidebarFolderActions({
    section: 'agents',
    refresh: refreshSidebar,
  });
  const { handleAddFolder, handleFolderRename } = folderActions;

  const handleAddItem = useCallback((folder: Folder) => {
    const reservedType = getReservedFolderType(folder);
    const spaceId = folder.space || undefined;
    if (reservedType === 'fluxbots' || reservedType === 'chatbot') {
      setFluxbotCreateRequest({ spaceId });
      return;
    }
    if (reservedType === 'vector memory') {
      setVectorCreateRequest({ spaceId });
      return;
    }
    if (reservedType === 'objects') {
      setCreateItemRequest({ type: 'object', spaceId });
      return;
    }
    setCreateItemRequest({ type: 'agent', folderId: folder.id, spaceId });
  }, []);

  const handleAddUsers = useCallback((spaceId: string) => {
    setAddUsersSpaceId(spaceId);
  }, []);

  const handleCopySpaceId = useCallback((space: Space & { email?: string }) => {
    setCopySpaceIdModalSpace({
      name: space.name,
      spaceId: space.email ?? space.id,
    });
  }, []);

  const {
    hasSearchTerm,
    allItems,
    privateAgentsStructure,
    privateSharedFolder,
    searchResult,
    getSpaceSectionData,
  } = sidebarData;

  const privateEnrichedSubfolders = useMemo(
    () =>
      enrichFoldersWithItems(
        privateAgentsStructure.subfolders,
        privateItemsRaw.objects,
        privateItemsRaw.vectorMemory,
        null,
      ),
    [
      privateAgentsStructure.subfolders,
      privateItemsRaw.objects,
      privateItemsRaw.vectorMemory,
    ],
  );

  const privateEnrichedSharedFolder = useMemo(
    () =>
      privateSharedFolder
        ? enrichFoldersWithItems(
            [privateSharedFolder],
            privateItemsRaw.objects,
            privateItemsRaw.vectorMemory,
            null,
          )[0]
        : null,
    [
      privateSharedFolder,
      privateItemsRaw.objects,
      privateItemsRaw.vectorMemory,
    ],
  );

  const contextValue = useMemo<AgentsSidebarContextValue>(
    () => ({
      isDarkMode,
      palette,
      activeFlowId,
      activeObjectId,
      onFlowClick: handleFlowClick,
      onObjectClick: handleObjectClick,
    }),
    [
      isDarkMode,
      palette,
      activeFlowId,
      activeObjectId,
      handleFlowClick,
      handleObjectClick,
    ],
  );

  const handleAddSubfolder = useCallback(
    (parentFolder: Folder) => {
      handleAddFolder({
        parentId: parentFolder.id,
        spaceId: parentFolder.space || undefined,
      });
    },
    [handleAddFolder],
  );

  const folderItemProps = useMemo(
    () => ({
      onFlowRename: handlers.handleFlowRename,
      onFlowDelete: handlers.handleFlowDelete,
      onFlowShare: handlers.handleFlowShare,
      onFlowOpenInNewTab: handlers.handleFlowOpenInNewTab,
      onFlowCreateInterface: handlers.handleCreateInterface,
      onFlowAddToLibrary: handlers.handleAddToLibrary,
      onFlowDuplicate: handlers.handleFlowDuplicate,
      onFolderRename: handleFolderRename,
      onFolderDelete: folderActions.handleFolderDelete,
      onAddSubfolder: handleAddSubfolder,
      onAddAgent: handleAddItem,
      onObjectEdit: handlers.handleObjectEdit,
      onObjectDelete: handlers.handleObjectDelete,
      onVectorEdit: handlers.handleVectorMemoryEdit,
      onVectorDelete: handlers.handleVectorMemoryDelete,
    }),
    [
      handlers,
      handleFolderRename,
      handleAddSubfolder,
      handleAddItem,
      folderActions.handleFolderDelete,
    ],
  );

  const spacesWithItems = useMemo(
    () =>
      spaces.map((space, index) => ({
        space,
        spaceItems: allSpacesItemsRaw[index],
      })),
    [spaces, allSpacesItemsRaw],
  );
  const sortedSpaces = useSortedSpaces(
    spacesWithItems,
    useCallback(({ space }) => !!space.disabled, []),
  );

  if (sidebarLoading && folders.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '24px' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const showPrivateDataSection =
    privateItemsRaw.objects.length > 0 ||
    privateItemsRaw.vectorMemory.length > 0 ||
    privateItemsRaw.fluxbots.length > 0;

  return (
    <AgentsSidebarContext.Provider value={contextValue}>
      <AgentsSidebarModals
        spaces={spaces}
        authUserTheme={authUser?.theme}
        modals={modals}
        handlers={handlers}
        performVectorMemoryDelete={performVectorMemoryDelete}
        setEditingObjectAndClose={setEditingObjectAndClose}
        setEditingVectorMemoryAndClose={setEditingVectorMemoryAndClose}
        folderActions={folderActions}
        refreshSidebar={refreshSidebar}
        resetObjectUpdate={resetObjectUpdate}
        setAuthUser={setAuthUser}
        showTemplatesModal={showTemplatesModal}
        setShowTemplatesModal={setShowTemplatesModal}
        addUsersSpaceId={addUsersSpaceId}
        setAddUsersSpaceId={setAddUsersSpaceId}
        copySpaceIdModalSpace={copySpaceIdModalSpace}
        setCopySpaceIdModalSpace={setCopySpaceIdModalSpace}
        showUsersAddedModal={showUsersAddedModal}
        setShowUsersAddedModal={setShowUsersAddedModal}
        pendingVectorNamespace={pendingVectorNamespace}
        setPendingVectorNamespace={setPendingVectorNamespace}
        createItemRequest={createItemRequest}
        setCreateItemRequest={setCreateItemRequest}
        vectorCreateRequest={vectorCreateRequest}
        setVectorCreateRequest={setVectorCreateRequest}
        fluxbotCreateRequest={fluxbotCreateRequest}
        setFluxbotCreateRequest={setFluxbotCreateRequest}
      />

      <Box
        sx={{
          padding: '8px',
          borderBottom: `1px solid ${
            isDarkMode ? palette.gray[700] : palette.gray[200]
          }`,
          marginBottom: '8px',
        }}
      >
        <Item
          icon={<Stack size={16} />}
          label='Templates'
          onClick={() => setShowTemplatesModal(true)}
        />
      </Box>

      <DragDropProvider>
        <Box
          ref={setScrollRef}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {hasSearchTerm && searchResult && allItems ? (
            <>
              {searchResult.filteredSubfolders.map(subfolder => (
                <FolderItem
                  key={subfolder.id}
                  folder={subfolder}
                  {...folderItemProps}
                />
              ))}
              {searchResult.filteredRootAgents.map(agent => (
                <AgentItem
                  key={agent.id}
                  flow={agent}
                  folderId='search-Agents'
                  onRename={handlers.handleFlowRename}
                  onDelete={handlers.handleFlowDelete}
                  onShare={handlers.handleFlowShare}
                  onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                  onCreateInterface={handlers.handleCreateInterface}
                  onAddToLibrary={handlers.handleAddToLibrary}
                  onDuplicate={handlers.handleFlowDuplicate}
                />
              ))}
              {searchResult.filteredRootForms?.map(form => (
                <AgentItem
                  key={form.id}
                  flow={form}
                  folderId='search-Agents'
                  onRename={handlers.handleFlowRename}
                  onDelete={handlers.handleFlowDelete}
                  onShare={handlers.handleFlowShare}
                  onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                  onCreateInterface={handlers.handleCreateInterface}
                  onAddToLibrary={handlers.handleAddToLibrary}
                  onDuplicate={handlers.handleFlowDuplicate}
                />
              ))}
              {allItems.fluxbots.length > 0 && (
                <CollapsibleGroup
                  icon={FolderLock}
                  title='Fluxbots'
                  action={
                    <SidebarAddButton
                      onClick={() => setFluxbotCreateRequest({})}
                    />
                  }
                >
                  {allItems.fluxbots.map(flow => (
                    <AgentItem
                      key={flow.id}
                      flow={flow}
                      folderId='search-Fluxbots'
                      forceFluxbotIcon
                      isNested
                      onRename={handlers.handleFlowRename}
                      onDelete={handlers.handleFlowDelete}
                      onShare={handlers.handleFlowShare}
                      onOpenInNewTab={handlers.handleFlowOpenInNewTab}
                      onCreateInterface={handlers.handleCreateInterface}
                      onAddToLibrary={handlers.handleAddToLibrary}
                      onDuplicate={handlers.handleFlowDuplicate}
                    />
                  ))}
                </CollapsibleGroup>
              )}
              {allItems.objects.map((obj: FluxObject) => (
                <FluxBotItem
                  key={obj.id}
                  object={obj}
                  onEdit={handlers.handleObjectEdit}
                  onRename={handlers.handleObjectRename}
                  onDelete={handlers.handleObjectDelete}
                  isNested
                />
              ))}
              {allItems.vectorMemory.length > 0 && (
                <CollapsibleGroup
                  icon={FolderLock}
                  title='Vector Memory'
                  action={
                    <SidebarAddButton
                      onClick={() => setVectorCreateRequest({})}
                    />
                  }
                >
                  {allItems.vectorMemory.map(ns => (
                    <VectorMemoryItem
                      key={ns.id}
                      namespace={ns}
                      onEdit={handlers.handleVectorMemoryEdit}
                      onDelete={handlers.handleVectorMemoryDelete}
                    />
                  ))}
                </CollapsibleGroup>
              )}
            </>
          ) : (
            <>
              <Section
                title='Private'
                hasBorder={spaces.length > 0 || showPrivateDataSection}
                isAccordion
                defaultExpanded
                onAddFolder={() => handleAddFolder({})}
              >
                <AgentsSectionBody
                  spaceId={null}
                  spaces={spaces}
                  rootFluxbots={privateFluxbots}
                  rootAgents={privateAgentsStructure.rootAgents}
                  rootForms={privateAgentsStructure.rootForms ?? []}
                  enrichedSharedFolder={privateEnrichedSharedFolder}
                  enrichedSubfolders={privateEnrichedSubfolders}
                  folderItemProps={folderItemProps}
                  handlers={handlers}
                  onCreateFluxbot={() => setFluxbotCreateRequest({})}
                  onCreateAgent={() => setCreateItemRequest({ type: 'agent' })}
                  onCreateInterface={() =>
                    setCreateItemRequest({ type: 'interface' })
                  }
                />
              </Section>

              {sortedSpaces.map(({ space, spaceItems }, index, sorted) => {
                const { agentsStructure, sharedFolderToShow } =
                  getSpaceSectionData(space, spaceItems);
                const enrichedSubfolders = enrichFoldersWithItems(
                  agentsStructure.subfolders,
                  spaceItems.objects,
                  spaceItems.vectorMemory,
                  space.id,
                );
                const enrichedSharedFolder = enrichFoldersWithItems(
                  [sharedFolderToShow],
                  spaceItems.objects,
                  spaceItems.vectorMemory,
                  space.id,
                )[0];
                return (
                  <Section
                    key={space.id}
                    title={space.name}
                    spaceId={space.id}
                    hasBorder={index < sorted.length - 1}
                    isAccordion
                    isDisabled={!!space.disabled}
                    onAddFolder={() => handleAddFolder({ spaceId: space.id })}
                    onEditSpace={() => handlers.handleEditSpace(space.id)}
                    onArchive={() => handlers.handleArchiveSpace(space.id)}
                    onAddUsers={() => handleAddUsers(space.id)}
                    onCopySpaceId={() => handleCopySpaceId(space)}
                  >
                    <AgentsSectionBody
                      spaceId={space.id}
                      spaces={spaces}
                      rootFluxbots={fluxbotsBySpaceId[space.id] ?? []}
                      rootAgents={agentsStructure.rootAgents}
                      rootForms={agentsStructure.rootForms ?? []}
                      enrichedSharedFolder={enrichedSharedFolder}
                      enrichedSubfolders={enrichedSubfolders}
                      folderItemProps={folderItemProps}
                      handlers={handlers}
                      onCreateFluxbot={() =>
                        setFluxbotCreateRequest({ spaceId: space.id })
                      }
                      onCreateAgent={() =>
                        setCreateItemRequest({
                          type: 'agent',
                          spaceId: space.id,
                        })
                      }
                      onCreateInterface={() =>
                        setCreateItemRequest({
                          type: 'interface',
                          spaceId: space.id,
                        })
                      }
                    />
                  </Section>
                );
              })}
            </>
          )}
        </Box>
      </DragDropProvider>
    </AgentsSidebarContext.Provider>
  );
};
